import { useEffect } from 'react'

import PropTypes from 'prop-types'
import { useFormContext, useWatch } from 'react-hook-form'

import { PhoneField } from '@/shared/ui/Form'
import getRules from '@/shared/ui/Form/lib/rules'

import FormControl from './FormControl'


/**
 * Telefone do participante.
 *
 * A máscara e o valor em dígitos puros vêm da camada canônica — `PhoneField`
 * em modo `unmasked`. O que fica aqui é o que é contrato de API e não
 * formatação: dividir o telefone completo em `phone_prefix` (DDD) e `phone`
 * (número), e manter os dois lados sincronizados quando o formulário abre já
 * preenchido por um deles.
 *
 * Não há `useController` neste componente de propósito: quem registra o campo
 * é o `MaskedField` dentro do `PhoneField`. Registrar de novo aqui faria os
 * dois registros disputarem o `ref` do mesmo `<input>`, e o sintoma disso é
 * justamente o modo de falha silenciosa que esta camada existe para eliminar.
 * A leitura é por `useWatch`; a escrita, só por `setValue`.
 */
const PhoneInput = ({
  name,
  phonePrefixName = 'phone_prefix',
  phoneName = 'phone',
  size,
  className,
  required,
  min,
  max,
  minLength,
  maxLength,
  pattern,
  validate,
  onChange,
  ...rest
}) => {
  const { control, setValue, getValues } = useFormContext()

  const value = useWatch({ control, name })
  const watchPhonePrefix = getValues(phonePrefixName)
  const watchPhone = getValues(phoneName)

  const handleChange = event => {
    // O modo `unmasked` já entregou os dígitos ao formulário; o nó segue
    // mascarado. A divisão opera sobre os dígitos, não sobre o que está na tela.
    const fullPhone = (event?.target?.value || '').replace(/\D/g, '')

    onChange && onChange(fullPhone)

    setValue(phonePrefixName, fullPhone.slice(0, 2))
    setValue(phoneName, fullPhone.slice(2))
  }

  useEffect(() => {
    if (value && !watchPhonePrefix && !watchPhone) {
      setValue(phonePrefixName, value.slice(0, 2))
      setValue(phoneName, value.slice(2))
    } else if (!value && watchPhonePrefix && watchPhone) {
      setValue(name, `${watchPhonePrefix}${watchPhone}`.replace(/\D/g, ''))
    }
  }, [value, watchPhonePrefix, watchPhone])

  return (
    <>
      <PhoneField
        unmasked
        name={name}
        size={size}
        className={className}
        rules={getRules({
          required,
          min,
          max,
          minLength,
          maxLength,
          pattern,
          validate
        })}
        onChange={handleChange}
        {...rest}
      />
      <FormControl name={phoneName} hidden />
      <FormControl name={phonePrefixName} hidden />
    </>
  )
}

PhoneInput.propTypes = {
  name: PropTypes.string.isRequired
}

export { PhoneInput }
export default PhoneInput
